// Browser and Node.js compatible logger
// All Node.js specific functionality is conditionally executed
export var LogLevel;
(function (LogLevel) {
    LogLevel[LogLevel["DEBUG"] = 0] = "DEBUG";
    LogLevel[LogLevel["INFO"] = 1] = "INFO";
    LogLevel[LogLevel["WARN"] = 2] = "WARN";
    LogLevel[LogLevel["ERROR"] = 3] = "ERROR";
})(LogLevel || (LogLevel = {}));
export class Logger {
    logLevel;
    consoleOutput;
    constructor(options = {}) {
        this.logLevel = options.level ?? LogLevel.INFO;
        this.consoleOutput = options.consoleOutput ?? true;
    }
    async init() {
        // No initialization needed for browser/Workers environment
        // This method exists for API compatibility
    }
    debug(message, data) {
        this.log(LogLevel.DEBUG, message, data);
    }
    info(message, data) {
        this.log(LogLevel.INFO, message, data);
    }
    warn(message, data) {
        this.log(LogLevel.WARN, message, data);
    }
    error(message, data) {
        this.log(LogLevel.ERROR, message, data);
    }
    log(level, message, data) {
        if (level < this.logLevel) {
            return;
        }
        const entry = {
            timestamp: new Date().toISOString(),
            level,
            message,
            data,
            source: "dmtrigger",
        };
        const formattedMessage = this.formatLogEntry(entry);
        if (this.consoleOutput) {
            this.outputToConsole(entry, formattedMessage);
        }
        // Async logging to file (don't await to avoid blocking)
        this.writeToFile(formattedMessage).catch((error) => {
            console.error("Failed to write to log file:", error);
        });
    }
    formatLogEntry(entry) {
        const levelNames = {
            [LogLevel.DEBUG]: "DEBUG",
            [LogLevel.INFO]: "INFO",
            [LogLevel.WARN]: "WARN",
            [LogLevel.ERROR]: "ERROR",
        };
        let formatted = `[${entry.timestamp}] ${levelNames[entry.level]}: ${entry.message}`;
        if (entry.data && Object.keys(entry.data).length > 0) {
            formatted += ` ${JSON.stringify(entry.data)}`;
        }
        return formatted;
    }
    outputToConsole(entry, formattedMessage) {
        switch (entry.level) {
            case LogLevel.DEBUG:
                console.debug(formattedMessage);
                break;
            case LogLevel.INFO:
                console.info(formattedMessage);
                break;
            case LogLevel.WARN:
                console.warn(formattedMessage);
                break;
            case LogLevel.ERROR:
                console.error(formattedMessage);
                break;
        }
    }
    async writeToFile(_message) {
        // File logging is not available in browser/Workers environment
        // This method exists for API compatibility but does nothing
    }
    // Utility method to get recent logs
    async getRecentLogs(_lines = 100) {
        // Log retrieval is not available in browser/Workers environment
        return [];
    }
    // Set log level dynamically
    setLogLevel(level) {
        this.logLevel = level;
    }
}
// Environment-aware logger creation (browser-safe)
function createLoggerConfig() {
    // Simple configuration for browser/Workers environment
    return {
        level: LogLevel.INFO,
        consoleOutput: true,
    };
}
// Global logger instance
export const logger = new Logger(createLoggerConfig());
// Initialize logger (safe for both environments)
if (typeof globalThis !== "undefined") {
    logger.init().catch((error) => {
        console.error("Failed to initialize logger:", error instanceof Error ? error.message : "Unknown error");
    });
}
//# sourceMappingURL=logger.js.map